import { Plugin, getCurrentInstance } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import { DateTime } from 'luxon';
import { formatDateTime, baseurl, handleException } from '@/helpers/Utils';

export const useMixins = (): {$handleException: any, $redirect: any, $silentRedirect: any, $back: any, $filters: any, $abort: any} =>
{
    const app = getCurrentInstance();

    return {
        $handleException: app.appContext.config.globalProperties.handleException,
        $redirect: app.appContext.config.globalProperties.redirect,
        $silentRedirect: app.appContext.config.globalProperties.silentRedirect,
        $back: app.appContext.config.globalProperties.back,
        $filters: app.appContext.config.globalProperties.$filters,
        $abort: app.appContext.config.globalProperties.abort
    };
};

interface Filters
{
    datetime(value: DateTime|Date|string, format?: string, zone?: string): string;
    date(value: DateTime|Date|string, format?: string, zone?: string): string;
    time(value: DateTime|Date|string, format?: string, zone?: string): string;
    baseurl(value: string): string;
    image(value: string, presetName: string): string;
    filesize(size: number): string;
    default(value: string, defaultValue: string): string;
    substring(value: string, size: number): string
}

class FiltersHelper implements Filters
{
    public datetime(value: DateTime|Date|string, format: string = 'yyyy-MM-dd HH:mm:ss', zone: string = null): string
    {
        return formatDateTime(value, format, zone);
    }

    public date(value: DateTime|Date|string, format: string = 'yyyy-MM-dd', zone: string = null): string
    {
        return formatDateTime(value, format, zone);
    }

    public time(value: DateTime|Date|string, format: string = 'HH:mm:ss', zone: string = null): string
    {
        return formatDateTime(value, format, zone);
    }

    public baseurl(value: string): string
    {
        return baseurl(value);
    }

    public image(value: string, presetName: string): string
    {
        return baseurl(`${value}?p=${presetName}`);
    }

    public filesize(size: number): string
    {
        const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        let i = 0;

        for (i = 0; size > 1024; i++)
        {
            size = size / 1024;
        }

        return `${units[i] == 'B' ? size : size.toFixed(2)} ${units[i]}`;
    }

    public substring(value: string, size: number): string
    {
        if (!value) return '';

        value = value.toString();

        if (value.length <= size)
        {
            return value;
        }

        return value.substr(0, size) + '...';
    }

    public default(value: string, defaultValue: string): string
    {
        return value || defaultValue;
    }
}

const MixinsPlugin: Plugin =
{
    install(app, options)
    {
        const vue = app.config.globalProperties;

        // Helpers
        vue.abort = (code: number, message: string): void =>
        {
            throw (() =>
            {
                return {
                    code: code,
                    message: message,
                    data: null as any,
                    inner: null as any
                };
            })();
        };

        vue.redirect = (location: RouteLocationRaw, onComplete?: () => void): void =>
        {
            vue.$router.push(location).catch(() => {}).finally(() => onComplete ? onComplete() : null);
        };

        vue.back = (): void =>
        {
            const { back, current } = vue.$router.options.history.state ?? {};

            if (back === current)
                vue.$router.push({ name: 'dashboard' });
            else
                vue.$router.back();
        };

        vue.silentRedirect = (location: RouteLocationRaw): void =>
        {
            const result = vue.$router.resolve(location);

            history.replaceState(history.state, '', result.href);
        };

        vue.handleException = (ex: any, rules: Record<number, (ex: any) => void>): void => handleException(vue.$log, ex, rules, vue.$events);

        // Filters
        vue.$filters = new FiltersHelper();
    }
};

export default MixinsPlugin;

declare module "@vue/runtime-core"
{
    interface ComponentCustomProperties
    {
        // Computed properties
        mq: string;
        phone: boolean;
        tablet: boolean;
        mobile: boolean;
        laptop: boolean;
        desktop: boolean;

        // Helpers
        abort(code: number, message: string): void;
        redirect(location: RouteLocationRaw, onComplete?: () => void): void;
        back(): void;
        silentRedirect(location: RouteLocationRaw): void;
        handleException(ex: any, rules: Record<number, (ex: any) => void>): void;

        // Filters
        $filters: Filters;
    }
}
